import { EventHandler, isNullOrUndefined as isNOU, updateCSSText } from '@syncfusion/ej2-base';
import { getAdjacentBlock, getBlockContentElement, getBlockIndexById, getBlockModelById, isListTypeBlock } from '../../common/utils/block';
import { cleanupElement } from '../../common/utils/common';
import { getSelectedRange } from '../../common/utils/selection';
import * as constants from '../../common/constant';
import { BlockType } from '../../models/enums';
import { events } from '../../common/constant';
/**
 * Drag and Drop module is used to perform block reordering actions.
 */
export class DragAndDropAction {
    constructor(manager) {
        this.isDragCompleted = false;
        this.isDragMoveCancelled = false;
        this.draggedBlocks = [];
        this.isIndicatorAtTop = false;
        this.parent = manager;
    }
    wireDragEvents() {
        EventHandler.add(this.parent.rootEditorElement, 'dragover', this.updateCurrentDroppingTarget, this);
        EventHandler.add(this.parent.rootEditorElement, 'dragenter', this.preventNoDropIcon, this);
        if (!isNOU(this.parent.floatingIconAction.floatingIconContainer)) {
            const dragIcon = this.parent.floatingIconAction.floatingIconContainer.querySelector('.e-block-drag-icon');
            if (!isNOU(dragIcon)) {
                EventHandler.add(dragIcon, 'dragstart', this.handleDragStart, this);
                EventHandler.add(dragIcon, 'drag', this.handleDragMove, this);
                EventHandler.add(dragIcon, 'dragend', this.handleDragStop, this);
            }
        }
        this.parent.observer.on(events.destroy, this.destroy, this);
    }
    unwireDragEvents() {
        EventHandler.remove(this.parent.rootEditorElement, 'dragover', this.updateCurrentDroppingTarget);
        EventHandler.remove(this.parent.rootEditorElement, 'dragenter', this.preventNoDropIcon);
        if (!isNOU(this.parent.floatingIconAction.floatingIconContainer)) {
            const dragIcon = this.parent.floatingIconAction.floatingIconContainer.querySelector('.e-block-drag-icon');
            if (!isNOU(dragIcon)) {
                EventHandler.remove(dragIcon, 'dragstart', this.handleDragStart);
                EventHandler.remove(dragIcon, 'drag', this.handleDragMove);
                EventHandler.remove(dragIcon, 'dragend', this.handleDragStop);
            }
        }
        this.parent.observer.off(events.destroy, this.destroy);
    }
    preventNoDropIcon(e) {
        e.preventDefault();
    }
    updateCurrentDroppingTarget(e) {
        e.preventDefault();
        const elementsAtPoint = document.elementsFromPoint(e.clientX, e.clientY);
        const innerMostElement = elementsAtPoint[0];
        const closestBlock = innerMostElement.closest('.' + constants.BLOCK_CLS);
        if (innerMostElement && closestBlock) {
            this.currentDropTarget = closestBlock;
        }
        else {
            this.currentDropTarget = null;
        }
    }
    handleDragMove(e) {
        if (isNOU(this.parent.currentHoveredBlock)) {
            return;
        }
        this.isDragCompleted = false;
        const dropIndex = this.currentDropTarget ? getBlockIndexById(this.currentDropTarget.id, this.parent.getEditorBlocks()) : -1;
        const eventArgs = {
            blocks: this.draggedBlocks,
            fromIndex: this.draggedBlocks.map((block) => getBlockIndexById(block.id, this.parent.getEditorBlocks())),
            dropIndex: dropIndex,
            event: e,
            target: this.currentDropTarget,
            cancel: false
        };
        this.parent.observer.notify('blockDragging', eventArgs);
        if (eventArgs.cancel) {
            this.isDragMoveCancelled = true;
            return;
        }
        else {
            this.isDragMoveCancelled = false;
        }
        if (this.dragClone) {
            const editorRect = this.parent.rootEditorElement.getBoundingClientRect();
            updateCSSText(this.parent.floatingIconAction.floatingIconContainer, 'display: flex;');
            const dragIcon = this.parent.floatingIconAction.floatingIconContainer.querySelector('.e-block-drag-icon');
            const dragIconRect = dragIcon.getBoundingClientRect();
            updateCSSText(this.parent.floatingIconAction.floatingIconContainer, 'display: none;');
            const scrollTop = this.parent.rootEditorElement.scrollTop;
            const scrollLeft = this.parent.rootEditorElement.scrollLeft;
            const totalHeight = Array.from(this.dragClone.children)
                .map((child) => child.offsetHeight)
                .reduce((sum, height) => sum + height, 0);
            const cssText = `opacity: 0.7; left: ${e.clientX - editorRect.left + dragIconRect.width + scrollLeft}px; top: ${e.clientY - editorRect.top + scrollTop}px; width: ${this.parent.currentHoveredBlock.offsetWidth}px; height: ${totalHeight}px;`;
            updateCSSText(this.dragClone, cssText);
            if (e.clientY < editorRect.top ||
                e.clientY > editorRect.bottom ||
                e.clientX < editorRect.left ||
                e.clientX > editorRect.right) {
                updateCSSText(this.dragClone, 'opacity: 0;');
            }
        }
        // To prevent the flickering of the drop indicator when drag towards up
        setTimeout(() => {
            this.updateDropIndicator();
        }, 50);
    }
    handleDragStart(e) {
        this.isDragCompleted = false;
        this.isDragMoveCancelled = false;
        const editorBlocks = this.parent.getEditorBlocks();
        if (!e.target.classList.contains('e-block-drag-icon') || !this.parent.currentHoveredBlock) {
            return;
        }
        let selectedBlocks = [];
        const hoveredBlockId = this.parent.currentHoveredBlock.id;
        let isHoveredBlockInSelection = false;
        if (this.parent.editorMethods.getSelectedBlocks()) {
            isHoveredBlockInSelection = this.parent.editorMethods.getSelectedBlocks()
                .some((block) => block.id === hoveredBlockId);
        }
        const range = getSelectedRange();
        if (range && range.toString().trim().length > 0 && isHoveredBlockInSelection) {
            selectedBlocks = this.parent.editorMethods.getSelectedBlocks();
        }
        else {
            this.parent.nodeSelection.clearSelection();
            const blockModel = getBlockModelById(hoveredBlockId, editorBlocks);
            if (blockModel) {
                selectedBlocks.push(blockModel);
            }
        }
        this.draggedBlocks = selectedBlocks;
        this.filterDraggedBlocksToExcludeChildren();
        const eventArgs = {
            blocks: this.draggedBlocks,
            fromIndex: this.draggedBlocks.map((block) => getBlockIndexById(block.id, editorBlocks)),
            dropIndex: this.currentDropTarget ? getBlockIndexById(this.currentDropTarget.id, editorBlocks) : -1,
            event: e,
            target: this.currentDropTarget,
            cancel: false
        };
        this.parent.observer.notify('blockDragStart', eventArgs);
        if (eventArgs.cancel) {
            e.preventDefault();
            return;
        }
        this.dragClone = document.createElement('div');
        const cssText = 'position: absolute; opacity: 0; pointer-events: none;';
        updateCSSText(this.dragClone, cssText);
        this.dragClone.classList.add('e-be-dragging-clone');
        for (const block of selectedBlocks) {
            const blockElement = document.getElementById(block.id);
            if (blockElement) {
                this.dragClone.appendChild(blockElement.cloneNode(true));
            }
        }
        this.parent.blockContainer.appendChild(this.dragClone);
        const transparentImage = new Image();
        transparentImage.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw=='; // 1x1 transparent pixel
        e.dataTransfer.setDragImage(transparentImage, 0, 0);
    }
    handleDragStop(e) {
        if (isNOU(this.parent.currentHoveredBlock)) {
            return;
        }
        if (this.isDragMoveCancelled) {
            cleanupElement(this.dragClone);
            cleanupElement(this.dropIndicator);
            return;
        }
        e.preventDefault();
        this.isDragCompleted = true;
        let currentIndicatorBlock;
        if (this.dropIndicator) {
            currentIndicatorBlock = this.dropIndicator.closest('.' + constants.BLOCK_CLS);
        }
        let dropTarget;
        if (currentIndicatorBlock) {
            const hoveredBlockRect = this.parent.currentHoveredBlock.getBoundingClientRect();
            const isMovingDown = hoveredBlockRect.top < currentIndicatorBlock.getBoundingClientRect().top;
            if (!this.isIndicatorAtTop && !isMovingDown) {
                dropTarget = getAdjacentBlock(currentIndicatorBlock, 'next');
                if (isNOU(dropTarget)) {
                    dropTarget = currentIndicatorBlock;
                }
            }
            else {
                dropTarget = currentIndicatorBlock;
            }
        }
        const eventArgs = {
            blocks: this.draggedBlocks.map((block) => block),
            fromIndex: this.draggedBlocks.map((block) => getBlockIndexById(block.id, this.parent.getEditorBlocks())),
            dropIndex: dropTarget ? getBlockIndexById(dropTarget.id, this.parent.getEditorBlocks()) : -1,
            event: e,
            target: dropTarget
        };
        this.parent.observer.notify('blockDropped', eventArgs);
        cleanupElement(this.dragClone);
        cleanupElement(this.dropIndicator);
        if (dropTarget && dropTarget !== this.parent.currentHoveredBlock) {
            this.reorderBlocks(this.draggedBlocks, dropTarget);
            this.parent.listPlugin.recalculateMarkersForListItems();
        }
        this.draggedBlocks = [];
    }
    reorderBlocks(draggedBlocks, dropTarget) {
        this.parent.execCommand({ command: 'MoveBlock', state: {
                fromBlockIds: draggedBlocks.map((block) => block.id),
                toBlockId: dropTarget.id
            } });
    }
    filterDraggedBlocksToExcludeChildren() {
        const draggedBlockIds = new Set(this.draggedBlocks.map((block) => block.id));
        this.draggedBlocks = this.draggedBlocks.filter((block) => {
            // If block has a parentId and that parent is also in draggedBlocks, exclude this block
            return !block.parentId || !draggedBlockIds.has(block.parentId);
        });
    }
    isNestedBlockType(blockType) {
        const nestedBlockTypes = [
            BlockType.Quote,
            BlockType.Callout,
            BlockType.CollapsibleParagraph,
            BlockType.CollapsibleHeading
        ];
        return nestedBlockTypes.indexOf(blockType) > -1;
    }
    hasNestedBlockInDraggedBlocks() {
        return this.draggedBlocks.some((block) => this.isNestedBlockType(block.blockType));
    }
    isDropTargetInsideNestedBlock() {
        let currentElement = this.currentDropTarget;
        while (currentElement && currentElement !== this.parent.blockContainer) {
            const blockElement = currentElement.closest('.' + constants.BLOCK_CLS);
            if (blockElement) {
                const blockModel = getBlockModelById(blockElement.id, this.parent.getEditorBlocks());
                if (blockModel && this.isNestedBlockType(blockModel.blockType)) {
                    return true;
                }
            }
            currentElement = blockElement ? blockElement.parentElement : null;
        }
        return false;
    }
    updateDropIndicator() {
        if (this.isDragCompleted ||
            !this.currentDropTarget ||
            this.currentDropTarget === this.parent.currentHoveredBlock ||
            this.draggedBlocks.some((block) => block.id === this.currentDropTarget.id)) {
            cleanupElement(this.dropIndicator);
            return;
        }
        // Restrict drop indicator for nested block scenarios
        if (this.hasNestedBlockInDraggedBlocks() && this.isDropTargetInsideNestedBlock()) {
            cleanupElement(this.dropIndicator);
            return;
        }
        if (!this.dropIndicator) {
            this.dropIndicator = document.createElement('div');
            this.dropIndicator.classList.add('e-be-drop-indicator');
        }
        const hoverdBlockRect = this.parent.currentHoveredBlock.getBoundingClientRect();
        const dropTargetRect = this.currentDropTarget.getBoundingClientRect();
        const middleY = dropTargetRect.top + (dropTargetRect.height / 2);
        let draggedBlockRect;
        if (this.dragClone) {
            draggedBlockRect = this.dragClone.children[0].getBoundingClientRect();
        }
        if (isNOU(draggedBlockRect) || isNOU(hoverdBlockRect)) {
            return;
        }
        if (hoverdBlockRect.top > draggedBlockRect.top) {
            this.handleDraggingAbove(middleY, draggedBlockRect);
        }
        else {
            this.handleDraggingBelow(middleY, draggedBlockRect);
        }
        const currentIndicatorBlock = this.dropIndicator.closest('.' + constants.BLOCK_CLS);
        if (!currentIndicatorBlock) {
            return;
        }
        const indicatorBlockModel = getBlockModelById(currentIndicatorBlock.id, this.parent.getEditorBlocks());
        const specialTypes = [BlockType.Divider, BlockType.CollapsibleParagraph, BlockType.CollapsibleHeading,
            BlockType.Callout, BlockType.Quote, BlockType.Table, BlockType.Image, BlockType.Code];
        const isSpecialType = (specialTypes.indexOf(indicatorBlockModel.blockType) > -1);
        if (isSpecialType) {
            updateCSSText(this.dropIndicator, 'left: 46px;');
            return;
        }
        const blockContent = getBlockContentElement(currentIndicatorBlock);
        if (!blockContent) {
            return;
        }
        const leftOffset = blockContent.getBoundingClientRect().left - currentIndicatorBlock.getBoundingClientRect().left;
        updateCSSText(this.dropIndicator, `left: ${leftOffset}px;`);
    }
    handleDraggingAbove(middleY, draggedBlockRect) {
        if (draggedBlockRect && draggedBlockRect.top < middleY) {
            const adjecentBLockEle = this.currentDropTarget.previousElementSibling;
            if (adjecentBLockEle) {
                if (!this.checkAndInsertIndicatorInListBlock(adjecentBLockEle, true)) {
                    adjecentBLockEle.appendChild(this.dropIndicator);
                }
                this.isIndicatorAtTop = false;
            }
            else {
                if (!this.checkAndInsertIndicatorInListBlock(this.currentDropTarget, false)) {
                    this.currentDropTarget.prepend(this.dropIndicator);
                }
                this.isIndicatorAtTop = true;
            }
        }
    }
    handleDraggingBelow(middleY, draggedBlockRect) {
        if (draggedBlockRect && draggedBlockRect.top > middleY) {
            if (!this.checkAndInsertIndicatorInListBlock(this.currentDropTarget, true)) {
                this.currentDropTarget.appendChild(this.dropIndicator);
            }
            this.isIndicatorAtTop = false;
        }
    }
    checkAndInsertIndicatorInListBlock(element, isAfter) {
        if (isListTypeBlock(element.getAttribute('data-block-type'))) {
            const listItem = element.querySelector('li');
            if (listItem) {
                listItem.insertAdjacentElement(isAfter ? 'afterend' : 'beforebegin', this.dropIndicator);
                return true;
            }
        }
        return false;
    }
    destroy() {
        this.unwireDragEvents();
    }
}
